import React, { useEffect, useRef, useState } from 'react';
import { useLocale } from '../../i18n/LocaleContext';
import './BuildingViewer.scss';

// One live 3D store preview. three.js is pulled in with a dynamic import the
// first time a viewer actually starts — and a viewer only starts once it has
// scrolled near the screen — so pages that merely link to a building never
// download it. Unmounting disposes the WebGL context; pages keep at most one
// of these alive at a time.

/// The Outfino default store shipped with the portal (default store v5, LOD1,
/// meshopt — LOD1 carries every logo-shape variant, the double door and the
/// closed-state plaque, at ~94 KB). PUBLIC_URL keeps it reachable in the
/// Shopify-embedded build too.
export const GENERIC_STORE_URL = `${process.env.PUBLIC_URL || ''}/models/default_v5_lod1_meshopt.glb`;

let sceneModule = null;
function loadScene() {
  if (!sceneModule) {
    sceneModule = import(/* webpackChunkName: "store-scene" */ './storeScene').catch((e) => {
      sceneModule = null;
      throw e;
    });
  }
  return sceneModule;
}

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

let webglOk = null;
function webglAvailable() {
  if (webglOk !== null) return webglOk;
  try {
    const c = document.createElement('canvas');
    const gl = c.getContext('webgl2') || c.getContext('webgl');
    webglOk = !!gl;
    // Hand the probe context straight back — browsers cap live contexts.
    gl?.getExtension('WEBGL_lose_context')?.loseContext();
  } catch {
    webglOk = false;
  }
  return webglOk;
}

/**
 *   modelUrl   custom building GLB; omitted → the default store
 *   logoUrl / coverUrl / brandName → painted onto the signs
 *   logoShape  'round' | 'square' | 'rect' — the default store's logo plaque
 *   closed     show the closed ("Zárva") state (lights off, plaque on the door)
 *   logoShape / closed change live, without reloading the model
 *   fallbackImage  shown instead of the canvas when WebGL is unavailable
 *   zoom       allow wheel/pinch zoom (modal only)
 *   className  extra class on the frame (sets the aspect ratio)
 */
export default function BuildingViewer({
  modelUrl,
  logoUrl,
  coverUrl,
  brandName,
  fallbackImage,
  zoom = false,
  className = '',
  label,
  logoShape = 'rect',
  closed = false,
}) {
  const { t } = useLocale();
  const closedText = t('building.closedSignText');
  // The live viewer, and the latest live-updatable props for the mount to
  // start from (changing them must not reload the model).
  const viewerRef = useRef(null);
  const liveRef = useRef({ logoShape, closed, closedText });
  liveRef.current = { logoShape, closed, closedText };
  const hostRef = useRef(null);
  // three.js owns this node's children; React never renders into it.
  const stageRef = useRef(null);
  const [state, setState] = useState('idle'); // idle | loading | ready | error | nogl
  const [near, setNear] = useState(false);
  const generic = !modelUrl;
  const url = modelUrl || GENERIC_STORE_URL;

  // Start only when the frame is (nearly) on screen.
  useEffect(() => {
    const el = hostRef.current;
    if (!el || near) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setNear(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setNear(true);
      },
      { rootMargin: '200px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [near]);

  useEffect(() => {
    if (!near) return undefined;
    if (!webglAvailable()) {
      setState('nogl');
      return undefined;
    }
    let viewer = null;
    let cancelled = false;
    setState('loading');
    loadScene()
      .then(({ mountStoreViewer }) =>
        mountStoreViewer(stageRef.current, {
          modelUrl: url,
          generic,
          logoUrl,
          coverUrl,
          brandName,
          zoom,
          reduceMotion: prefersReducedMotion(),
          ...liveRef.current,
        })
      )
      .then((v) => {
        if (cancelled) {
          v.dispose();
          return;
        }
        viewer = v;
        viewerRef.current = v;
        // Props may have moved on while the model loaded.
        const live = liveRef.current;
        v.setLogoShape(live.logoShape);
        v.setClosed(live.closed);
        v.setClosedText(live.closedText);
        setState('ready');
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.warn('3D preview failed:', e);
        if (!cancelled) setState('error');
      });
    return () => {
      cancelled = true;
      viewerRef.current = null;
      if (viewer) viewer.dispose();
    };
  }, [near, url, generic, logoUrl, coverUrl, brandName, zoom]);

  useEffect(() => {
    viewerRef.current?.setLogoShape(logoShape);
  }, [logoShape]);
  useEffect(() => {
    viewerRef.current?.setClosed(closed);
  }, [closed]);
  useEffect(() => {
    viewerRef.current?.setClosedText(closedText);
  }, [closedText]);

  return (
    <div
      ref={hostRef}
      className={`store-viewer ${className}`}
      role="img"
      aria-label={label || t('building.viewerLabel', { name: brandName || '' })}
    >
      <div ref={stageRef} className="store-viewer__stage" />
      {state !== 'ready' && (
        <div className="store-viewer__status">
          {state === 'error' || state === 'nogl' ? (
            fallbackImage ? (
              <img className="store-viewer__fallback" src={fallbackImage} alt="" />
            ) : (
              <span>{t(state === 'nogl' ? 'building.viewerNoGl' : 'building.viewerError')}</span>
            )
          ) : (
            <span className="store-viewer__spinner" aria-hidden="true" />
          )}
        </div>
      )}
      {state === 'ready' && <span className="store-viewer__hint">{t('building.viewerHint')}</span>}
    </div>
  );
}
